import { getAllCustomers } from '@commenda/api';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';

export const useCustomerSearch = ({
  corporationId,
}: {
  corporationId: number;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [allCustomers, setAllCustomers] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const {
    customers,
    cursor: nextCursor,
    totalCustomers,
    isLoading,
    mutate: refetchCustomers,
  } = getAllCustomers({
    cursor,
    limit: 100,
    filters: { corporationId },
    name: searchTerm,
  });

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
      setCursor(null);
    }, 300),
    []
  );

  useEffect(() => {
    if (customers) {
      setAllCustomers((prev) => {
        const newCustomers = customers.map((c) => ({
          value: c.id,
          label: c.name,
        }));

        const addedCustomers = newCustomers.filter(
          (newCustomer) => !prev.some((c) => c.value === newCustomer.value)
        );

        // Returning prev unchanged lets React skip the re-render; a fresh
        // array here loops, since customers is a new [] on every render
        // until the list loads.
        return addedCustomers.length > 0
          ? [...prev, ...addedCustomers]
          : prev;
      });
    }
  }, [customers]);

  const handleSearch = (term: string) => {
    debouncedSearch(term);
  };

  const loadMore = () => {
    if (nextCursor) {
      setCursor(nextCursor);
    }
  };

  const hasMore =
    allCustomers.length < (totalCustomers || 0) && allCustomers.length < 100;

  return {
    customers: allCustomers,
    isLoading,
    handleSearch,
    loadMore,
    hasMore,
    refetchCustomers,
  };
};
